<template>
  <div class="p-2">
    <template v-if="authorizationError">
      <Message severity="error">
        {{
          $t(
            'Authorization failed. Please check that the Phottr plugin is installed and not paused.',
          )
        }}
      </Message>
    </template>
    <template v-else>
      <Message severity="error" v-if="error">
        {{ error }}
      </Message>

      <h2>{{ $t('New Connection') }}</h2>
      <p>
        {{
          $t(
            "Scan QR code with your phone's camera to automatically connect your e-shop with Phottr.",
          )
        }}
      </p>
      <Button class="mt-8" :loading="initiatingConnect" @click="connectNewAccountBranch">
        {{ $t('Connect') }}
      </Button>
      <br />
      <img v-if="qrCodeText" class="mt-6 mb-2 rounded border" :src="qrCode" alt="QR Code" />

      <br />

      <h2>{{ $t('Connected Accounts') }}</h2>
      <p>{{ $t('List of all accounts that are currently connected to this e-shop.') }}</p>
      <br />
      <table class="product-table">
        <thead>
          <tr>
            <th>{{ $t('Identification') }}</th>
            <th>{{ $t('Connected on') }}</th>
            <th>{{ $t('Remove') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="connection in connections" :key="connection.integrationId">
            <td>
              <template v-if="!connection.orgPersonal"> {{ connection.orgName }} / </template>
              {{ connection.identification }}
            </td>
            <td>{{ dayjs(connection.created).format('DD.MM.YYYY') }}</td>
            <td>
              <Button
                icon="pi pi-times"
                variant="text"
                severity="danger"
                size="small"
                rounded
                :loading="disconnecting === connection.integrationId"
                @click="disconnectAccount(connection.integrationId)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { ref } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { useQRCode } from '@vueuse/integrations/useQRCode';
import { useApiClient } from '@/api/apiClient';
import type { ShoptetConnectionDto } from '@/api/generated';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import { setLocaleAndLoadMessages } from '@/i18n';
import { useI18n } from 'vue-i18n';
import Message from 'primevue/message';
import Button from 'primevue/button';

const { api } = useApiClient();
const route = useRoute();
const i18n = useI18n();

const adminAccessToken = ref('');
const eshopName = ref('');
const connections = ref<ShoptetConnectionDto[]>([]);
const qrCodeText = ref('');
const qrCode = useQRCode(qrCodeText);
const qrCodeExpires = ref(0);
const initiatingConnect = ref(false);
const newIntegrationId = ref('');
const error = ref('');
const authorizationError = ref(false);
const pollingInterval = useIntervalFn(async () => {
  await loadConnections();
  if (connections.value.find(c => c.integrationId === newIntegrationId.value)) {
    pollingInterval.pause();
    qrCodeText.value = '';
    qrCodeExpires.value = 0;
    newIntegrationId.value = '';
  }
}, 5000);
const disconnecting = ref('');

async function setupI18n() {
  let locale;
  switch (route.query.language) {
    case 'cs':
    case 'sk':
      locale = 'cs';
      break;
    default:
      locale = 'en';
  }
  await setLocaleAndLoadMessages(i18n, locale);
}

await setupI18n();

async function verifyOauthCode(): Promise<boolean> {
  const res = await api.shoptetControllerVerifyCode({
    query: {
      eshopId: route.query.eshopId?.toString() ?? '',
      code: route.query.code as string,
    },
  });
  if (res.error) {
    authorizationError.value = true;
    return false;
  }
  adminAccessToken.value = res.data?.adminAccessToken ?? '';
  eshopName.value = res.data?.eshopName ?? '';
  return true;
}

async function loadConnections() {
  const res = await api.shoptetControllerListConnections({
    body: {
      eshopId: Number(route.query.eshopId),
      adminAccessToken: adminAccessToken.value,
    },
  });
  if (res.error) {
    error.value = 'Failed to load connections, try to refresh the page';
    return;
  }
  connections.value = res.data ?? [];
}

async function connectNewAccountBranch() {
  const { eshopId, code } = route.query;
  if (!eshopId || !code) {
    error.value = 'Invalid URL';
    return;
  }

  try {
    initiatingConnect.value = true;

    const res = await api.shoptetControllerInitiateConnect({
      body: {
        eshopId: Number(eshopId),
        adminAccessToken: adminAccessToken.value,
      },
    });

    if (res.error || !res.data) {
      error.value = 'Failed to get QR code from Phottr';
      return;
    }

    const { integrationId, secret, expires } = res.data;

    const data = {
      action: 'add-integration',
      type: 'shoptet',
      eshopId: Number(eshopId),
      secret: secret,
      eshopName: eshopName.value,
    };

    // TODO create link at backend to protect branch_key
    const branchRes = await axios.post('https://api2.branch.io/v1/url', {
      branch_key: 'key_live_gCEcyUMt81YslQTA3Jd9LjadCubzhr0g',
      channel: 'qr',
      feature: 'connect',
      campaign: 'shoptet-integration',
      data,
    });

    const url = new URL(branchRes.data.url);
    Object.entries(data).forEach(([key, value]) => {
      url.searchParams.set(key, value as string);
    });
    qrCodeText.value = url.toString();
    qrCodeExpires.value = expires;
    newIntegrationId.value = integrationId;

    pollingInterval.resume();
  } catch (err) {
    Sentry.captureException(err);
    error.value = 'Failed to get QR code from Phottr';
  } finally {
    initiatingConnect.value = false;
  }
}

async function disconnectAccount(integrationId: string) {
  try {
    // TODO: confirm disconnect

    disconnecting.value = integrationId;
    const res = await api.shoptetControllerDisconnect({
      body: {
        integrationId,
        eshopId: Number(route.query.eshopId),
        adminAccessToken: adminAccessToken.value,
      },
    });
    if (res.error) {
      error.value = 'Failed to disconnect account, try to refresh the page';
      return;
    }
    await loadConnections();
  } catch (err) {
    Sentry.captureException(err);
    error.value = 'Failed to disconnect account, try to refresh the page';
  } finally {
    disconnecting.value = '';
  }
}

if (await verifyOauthCode()) {
  loadConnections();
}
</script>

<style scoped>
h2 {
  font-size: 24px;
  font-weight: 700;
}

.connections {
  max-width: 400px;
}

table.product-table {
  width: 100%;
  border-collapse: collapse;
  font-family: Arial, sans-serif;
  font-size: 14px;
}

table.product-table thead {
  background-color: #f5f5f5;
  border-bottom: 2px solid #d0d0d0;
}

table.product-table th {
  text-align: left;
  padding: 10px;
  font-weight: bold;
  vertical-align: middle;
}

table.product-table tbody tr {
  border-bottom: 1px solid #e0e0e0;
  height: 60px;
}

table.product-table td {
  padding: 10px;
  vertical-align: middle;
}

ol {
  padding-left: 20px;
}

ol li {
  padding-left: 20px;
}
</style>
